import { useTranslation } from 'next-i18next';
import type { SelectOption } from '@hub/common/inputs/FilterSelectAutocomplete';
import { FilterSelectAutocomplete } from '@hub/common/inputs/FilterSelectAutocomplete';
import { useState } from 'react';
import { SortingState } from '@hub/libs/bearing-hub.helper';
import type { Headers } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import { useBearingsTableStore } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';

enum FilteringOptions {
  MATERIAL_ASCENDING = 'material-ascending',
  MATERIAL_DESCENDING = 'material-descending',
  DELIVERY_ASCENDING = 'delivery-ascending',
  DELIVERY_DESCENDING = 'delivery-descending',
  SERVICE_LIFE_ASCENDING = 'service-life-ascending',
  SERVICE_LIFE_DESCENDING = 'service-life-descending',
  PRICE_ASCENDING = 'price-ascending',
  PRICE_DESCENDING = 'price-descending',
}

interface MobileSortingOptions {
  label: string;
  value: FilteringOptions;
  sortingState: SortingState;
  columnKey: Headers;
}

export const ProductSort = () => {
  const { t } = useTranslation(['common', 'category/categoryPage']);
  const mobileSortingOptions = new Map([
    [
      FilteringOptions.MATERIAL_ASCENDING,
      {
        label: `${t(`HEADLINES.MATERIAL`, { ns: 'bearing-hub/bearingHub' })} (${t(`SORTING.ASCENDING`, {
          ns: 'bearing-hub/bearingHub',
        })})`,
        value: FilteringOptions.MATERIAL_ASCENDING,
        sortingState: SortingState.Ascending,
        columnKey: 'material',
      },
    ],
    [
      FilteringOptions.MATERIAL_DESCENDING,
      {
        label: `${t(`HEADLINES.MATERIAL`, { ns: 'bearing-hub/bearingHub' })} (${t(`SORTING.DESCENDING`, {
          ns: 'bearing-hub/bearingHub',
        })})`,
        value: FilteringOptions.MATERIAL_DESCENDING,
        sortingState: SortingState.Descending,
        columnKey: 'material',
      },
    ],
    [
      FilteringOptions.DELIVERY_ASCENDING,
      {
        label: `${t(`HEADLINES.DELIVERY_TIME`, { ns: 'bearing-hub/bearingHub' })} (${t(`SORTING.ASCENDING`, {
          ns: 'bearing-hub/bearingHub',
        })})`,
        value: FilteringOptions.DELIVERY_ASCENDING,
        sortingState: SortingState.Ascending,
        columnKey: 'deliveryTime',
      },
    ],
    [
      FilteringOptions.DELIVERY_DESCENDING,
      {
        label: `${t(`HEADLINES.DELIVERY_TIME`, { ns: 'bearing-hub/bearingHub' })} (${t(`SORTING.DESCENDING`, {
          ns: 'bearing-hub/bearingHub',
        })})`,
        value: FilteringOptions.DELIVERY_DESCENDING,
        sortingState: SortingState.Descending,
        columnKey: 'material',
      },
    ],
    [
      FilteringOptions.SERVICE_LIFE_ASCENDING,
      {
        label: `${t(`HEADLINES.LIFETIME`, { ns: 'bearing-hub/bearingHub' })} (${t(`SORTING.ASCENDING`, {
          ns: 'bearing-hub/bearingHub',
        })})`,
        value: FilteringOptions.SERVICE_LIFE_ASCENDING,
        sortingState: SortingState.Ascending,
        columnKey: 'lifetime',
      },
    ],
    [
      FilteringOptions.SERVICE_LIFE_DESCENDING,
      {
        label: `${t(`HEADLINES.LIFETIME`, { ns: 'bearing-hub/bearingHub' })} (${t(`SORTING.DESCENDING`, {
          ns: 'bearing-hub/bearingHub',
        })})`,
        value: FilteringOptions.SERVICE_LIFE_DESCENDING,
        sortingState: SortingState.Descending,
        columnKey: 'lifetime',
      },
    ],
    [
      FilteringOptions.PRICE_ASCENDING,
      {
        label: `${t(`HEADLINES.PRICE`, { ns: 'bearing-hub/bearingHub' })} (${t(`SORTING.ASCENDING`, {
          ns: 'bearing-hub/bearingHub',
        })})`,
        value: FilteringOptions.PRICE_ASCENDING,
        sortingState: SortingState.Ascending,
        columnKey: 'price',
      },
    ],
    [
      FilteringOptions.PRICE_DESCENDING,
      {
        label: `${t(`HEADLINES.PRICE`, { ns: 'bearing-hub/bearingHub' })} (${t(`SORTING.DESCENDING`, {
          ns: 'bearing-hub/bearingHub',
        })})`,
        value: FilteringOptions.PRICE_DESCENDING,
        sortingState: SortingState.Descending,
        columnKey: 'price',
      },
    ],
  ]);
  const filterOptions = Array.from(mobileSortingOptions.values()) as unknown as SelectOption[];
  const defaultFilterValue = mobileSortingOptions.get(FilteringOptions.PRICE_ASCENDING);
  const [filterValue, setFilterValue] = useState(defaultFilterValue);
  const { setTableHeadersSortingState } = useBearingsTableStore();

  const onFilterChange = (value: FilteringOptions) => {
    const option = mobileSortingOptions.get(value) as MobileSortingOptions;
    setFilterValue(option);
    if (!value) {
      return;
    }

    const { columnKey, sortingState } = option;
    setTableHeadersSortingState(columnKey, sortingState);
  };

  return (
    <div className="flex flex-col w-full">
      <label htmlFor="bearings-sorting" className="text-sm	block font-medium w-1/3 lg:w-7/12">
        {t(`SORTING.SORT_BY`, { ns: 'bearing-hub/bearingHub' })}:
      </label>
      <FilterSelectAutocomplete
        key="bearings-sorting"
        id="bearings-sorting"
        value={filterValue as unknown as SelectOption}
        onChange={value => onFilterChange(value as unknown as FilteringOptions)}
        options={filterOptions}
        hideOptionsModel
        placeholder={t(`SORTING.SORT_BY`, { ns: 'bearing-hub/bearingHub' })}
      />
    </div>
  );
};
